import * as z from "zod";

// Define care needs options
export const careNeeds = [
  { id: "general", label: "General Care" },
  { id: "dementia", label: "Dementia Care" },
  { id: "palliative", label: "Palliative Care" },
  { id: "respite", label: "Respite Care" },
  { id: "rehabilitation", label: "Rehabilitation" },
];

// Define timeline options
export const timelineOptions = [
  { id: "immediate", label: "Immediate (within 1 month)" },
  { id: "soon", label: "Soon (1-3 months)" },
  { id: "planning", label: "Planning ahead (3-6 months)" },
  { id: "researching", label: "Just researching (6+ months)" },
];

// Define salutation options
export const salutationOptions = [
  { id: "mr", label: "Mr." },
  { id: "ms", label: "Ms." },
  { id: "mrs", label: "Mrs." },
];

// Define Fair Deal Scheme options
export const fairDealOptions = [
  { id: "applied", label: "Yes - Applied/Confirmed" },
  { id: "need-info", label: "Yes - Need more information" },
  { id: "no", label: "No" },
];

// Define the validation schema
export const waitlistFormSchema = z.object({
  // Personal Details - All REQUIRED
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(7, { message: "Please enter a valid phone number" }),
  
  // Location & Preferences - Counties required, specific homes optional
  preferredLocation: z.array(z.string()).min(1, { message: "Please select at least one county" }),
  specificNursingHome: z.string().optional(),
  
  // Care Requirements - Care needs required, details optional
  careNeeds: z.array(z.string()).min(1, { message: "Please select at least one care need" }),
  careNeedsDetails: z.string().optional(),
  mobilityRequirements: z.string().optional(),
  
  // Resident Details - Relationship required, others conditional
  residentRelation: z.enum(["self", "parent", "spouse", "other"], {
    required_error: "Please select a relation",
  }),
  residentSalutation: z.enum(["mr", "ms", "mrs"], {
    required_error: "Please select a salutation",
  }).optional(),
  residentLastName: z.string().min(1, { message: "Resident last name is required" }).optional().transform(val => val === "" ? undefined : val),
  residentAge: z.string().min(1, { message: "Please enter the resident's age" }).optional().transform(val => val === "" ? undefined : val),
  
  // Timeline & Urgency - Required
  timeframe: z.enum(["immediate", "soon", "planning", "researching"], {
    required_error: "Please select a timeframe",
  }),
  
  // Financial Information - Required
  fairDealScheme: z.enum(["applied", "need-info", "no"], {
    required_error: "Please specify Fair Deal Scheme status",
  }),
  
  // Communication Preferences - Required
  contactPreference: z.enum(["email", "phone", "both"], {
    required_error: "Please select your preferred contact method",
  }),
  
  // Additional Information - Optional
  additionalInformation: z.string().optional(),
  
  // Terms consent - EXPLICITLY REQUIRED
  consentToPrivacyPolicy: z.boolean({
    required_error: "You must agree to the privacy policy",
  }).refine(val => val === true, {
    message: "You must agree to the privacy policy",
  }),
}).refine((data) => {
  // Existing refinement for resident last name
  if (data.residentRelation !== "self" && (!data.residentLastName || data.residentLastName.trim() === "")) {
    return false;
  }
  return true;
}, {
  message: "Please provide the resident's last name",
  path: ["residentLastName"]
});

export type WaitlistFormValues = z.infer<typeof waitlistFormSchema>;
